import { computed, DestroyRef, Injectable, inject, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, fromEvent, map, shareReplay, startWith } from 'rxjs';
import { WindowRefService } from '../../../services/window-ref.service';

/** Max width (px) at or below which layout is treated as mobile for offers/shared UI. */
export const MOBILE_MAX_WIDTH = 440;

/** Matches the `md` Sass breakpoint. Widths below this use the mobile toast layout. */
export const MEDIUM_BREAKPOINT = 775;

@Injectable({
  providedIn: 'root',
})
export class ScreenDimensionService {
  private readonly windowRef = inject(WindowRefService);

  private readonly windowWidth$ = fromEvent(this.windowRef.nativeWindow, 'resize').pipe(
    debounceTime(200),
    map(() => this.windowRef.nativeWindow.innerWidth as number),
    startWith(this.windowRef.nativeWindow.innerWidth as number),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly windowWidth = toSignal(this.windowWidth$);

  readonly isBelowMedium$ = this.windowWidth$.pipe(map((width) => width < MEDIUM_BREAKPOINT));

  readonly isMobile = computed(() => this.isMobileWidth(this.windowWidth()));

  isMobileWidth(width: number): boolean {
    return width <= MOBILE_MAX_WIDTH;
  }

  /** ResizeObserver-backed width; disconnects with `destroyRef`. No-op if ResizeObserver is missing. */
  createElementWidthSignal(element: HTMLElement, destroyRef: DestroyRef): WritableSignal<number> {
    const width = signal(element.getBoundingClientRect().width);
    const RO = globalThis.ResizeObserver;
    if (typeof RO !== 'function') {
      return width;
    }
    let alive = true;
    const ro = new RO((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w === undefined) {
        return;
      }
      queueMicrotask(() => {
        if (alive) {
          width.set(w);
        }
      });
    });
    ro.observe(element);
    destroyRef.onDestroy(() => {
      alive = false;
      ro.disconnect();
    });
    return width;
  }

  /** Synchronous viewport check (not debounced), e.g. one-off reads. */
  getIsMobileScreen(): boolean {
    return this.isMobileWidth(this.windowRef.nativeWindow.innerWidth);
  }
}
